import { assertValidBoard, countActiveCells, type BoardData } from '../model/board';
import { toHex64 } from '../model/hash';
import type { LevelIdentity } from '../model/identity';
import type { GeneratedLevel } from '../model/level';
import type { LevelRecipe } from '../model/recipe';
import { computeBoardHash } from '../model/serialization';
import { validateSolution } from '../rules/moves';
import { analyzeBoard, type PuzzleAnalysis } from '../solver/analysis';
import { canonicalSolution } from '../solver/solver';
import { getTuning } from './config/tuning';
import { bandForLevel, isTutorialLevel, planLevel, type LevelPlan } from './director';
import { Pcg32 } from './prng';
import { fillHoles } from './holeFilling';
import { buildCandidate } from './reverseConstruction';
import { scoreCandidate } from './scoring';
import { deriveRootSeed, deriveSubSeed, SUBSYSTEM } from './seeds';
import { getTutorialLevel, tutorialBoard } from './tutorial';

/**
 * Level generation orchestrator.
 *
 * Deterministic in every respect: a fixed candidate budget (never a time budget), fixed
 * per-candidate seeds, deterministic tie-breaking by candidate index, and a deterministic
 * fallback. Two devices with wildly different speeds always select the same candidate.
 */

export class GenerationError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'GenerationError';
    this.code = code;
  }
}

export interface CandidateDiagnostic {
  readonly index: number;
  readonly accepted: boolean;
  readonly score: number;
  readonly rejections: readonly string[];
  readonly arrowCount: number;
  readonly targetArrowCount: number;
  readonly placementAttempts: number;
  readonly repairPasses: number;
  readonly boardHash: string;
  readonly analysis?: PuzzleAnalysis;
}

export interface GenerationDiagnostics {
  readonly identity: LevelIdentity;
  readonly rootSeed: string;
  readonly recipe: LevelRecipe;
  readonly stencilAttempts: readonly string[];
  readonly activeCells: number;
  readonly candidates: readonly CandidateDiagnostic[];
  readonly chosenCandidateIndex: number;
  readonly usedFallback: boolean;
  /** Arrows added by the hole-filling pass after the candidate was chosen. */
  readonly holesFilled: number;
  readonly holeFillingTrials: number;
}

export interface GenerationResult {
  readonly level: GeneratedLevel;
  readonly diagnostics: GenerationDiagnostics;
}

export interface GenerateOptions {
  /** Include full analysis for every candidate. Developer tooling only; costs memory. */
  readonly collectCandidateAnalysis?: boolean;
}

export function generateLevel(
  identity: LevelIdentity,
  options: GenerateOptions = {},
): GenerationResult {
  if (isTutorialLevel(identity.levelNumber)) {
    return generateTutorialLevel(identity);
  }

  const tuning = getTuning(identity.contentVersion);
  const plan = planLevel(identity);
  const rootSeed = deriveRootSeed(identity);
  const recipe = plan.recipe;

  const candidates: CandidateDiagnostic[] = [];
  let best:
    { board: BoardData; analysis: PuzzleAnalysis; index: number; score: number } | undefined;
  let bestAccepted = false;

  for (let index = 0; index < recipe.candidateBudget; index += 1) {
    const rng = new Pcg32(deriveSubSeed(rootSeed, SUBSYSTEM.reverseConstruction, index));
    // Candidates explore slightly different densities so the search is not just re-rolling
    // the same board; the jitter is a pure function of the candidate index.
    const jitterRng = new Pcg32(deriveSubSeed(rootSeed, SUBSYSTEM.candidateRetry, index));
    const jitter = index === 0 ? 0 : jitterRng.nextInt(-2, 2);
    const targetOccupiedCells = clamp(
      plan.targetOccupiedCells + Math.round((plan.targetOccupiedCells * jitter) / 40),
      1,
      Math.min(plan.activeCells, tuning.maxOccupiedCells),
    );

    const candidate = buildCandidate({ mask: plan.mask, recipe, rng, targetOccupiedCells });
    const board = candidate.board;

    // The construction order is a proof of solvability; verifying it here catches any
    // future regression in the placement rules immediately rather than at play time.
    const proof = validateSolution(board, candidate.constructionSolution);
    if (!proof.valid) {
      candidates.push({
        index,
        accepted: false,
        score: 0,
        rejections: [`construction-proof-invalid:${proof.failureReason ?? 'unknown'}`],
        arrowCount: board.arrows.length,
        targetArrowCount: recipe.targetArrowCount,
        placementAttempts: candidate.attempts,
        repairPasses: candidate.repairPasses,
        boardHash: computeBoardHash(board),
      });
      continue;
    }

    const analysis = analyzeBoard(board);
    const scored = scoreCandidate(analysis, recipe, tuning);
    candidates.push({
      index,
      accepted: scored.accepted,
      score: scored.score,
      rejections: scored.rejections,
      arrowCount: board.arrows.length,
      targetArrowCount: recipe.targetArrowCount,
      placementAttempts: candidate.attempts,
      repairPasses: candidate.repairPasses,
      boardHash: computeBoardHash(board),
      ...(options.collectCandidateAnalysis === true ? { analysis } : {}),
    });

    const better =
      best === undefined ||
      (scored.accepted && !bestAccepted) ||
      (scored.accepted === bestAccepted && scored.score > best.score);
    if (better) {
      best = { board, analysis, index, score: scored.score };
      bestAccepted = scored.accepted;
    }
  }

  let usedFallback = false;
  if (best === undefined || !bestAccepted) {
    const fallback = generateFallbackCandidate(plan, rootSeed, tuning.maxOccupiedCells);
    if (fallback !== undefined) {
      const fallbackScore = scoreCandidate(fallback.analysis, recipe, tuning);
      candidates.push({
        index: -1,
        accepted: fallbackScore.accepted,
        score: fallbackScore.score,
        rejections: fallbackScore.rejections,
        arrowCount: fallback.board.arrows.length,
        targetArrowCount: recipe.targetArrowCount,
        placementAttempts: 0,
        repairPasses: 0,
        boardHash: computeBoardHash(fallback.board),
      });
      // The fallback only wins when no candidate was accepted and it is at least as good.
      if (best === undefined || (!bestAccepted && fallbackScore.score >= best.score)) {
        best = { ...fallback, index: -1, score: fallbackScore.score };
        usedFallback = true;
      }
    }
  }

  if (best === undefined || best.board.arrows.length === 0) {
    throw new GenerationError(
      'no-viable-candidate',
      `Unable to generate level ${identity.levelNumber}`,
    );
  }

  /*
   * Pack the gaps. Reverse construction stops when no cell has a clear corridor left, which
   * on a large board leaves obvious dead space; this fills it with arrows that may start
   * blocked, keeping only those the solver confirms still leave the board solvable.
   *
   * Run on the winner alone rather than on every candidate: it is comparatively expensive,
   * and it affects all candidates the same way, so it cannot change which one deserves to win.
   */
  const filled = fillHoles(best.board, new Pcg32(deriveSubSeed(rootSeed, SUBSYSTEM.holeFilling)));
  const filledAnalysis = analyzeBoard(filled.board);
  const useFilled = filled.added > 0 && filledAnalysis.solvable;

  const level = finaliseLevel({
    identity,
    rootSeed,
    recipe,
    board: useFilled ? filled.board : best.board,
    analysis: useFilled ? filledAnalysis : best.analysis,
    candidateIndex: best.index,
    usedFallback,
  });

  return {
    level,
    diagnostics: {
      identity,
      rootSeed: toHex64(rootSeed),
      recipe,
      stencilAttempts: plan.stencilAttempts,
      activeCells: plan.activeCells,
      candidates,
      chosenCandidateIndex: best.index,
      usedFallback,
      holesFilled: useFilled ? filled.added : 0,
      holeFillingTrials: filled.trials,
    },
  };
}

/**
 * Deterministic fallback: uniform placement at a relaxed target. It ignores archetype
 * shaping in exchange for reliably filling the silhouette, and is only ever used when no
 * scored candidate was acceptable.
 */
function generateFallbackCandidate(
  plan: LevelPlan,
  rootSeed: bigint,
  maxOccupiedCells: number,
): { board: BoardData; analysis: PuzzleAnalysis } | undefined {
  const relaxedRecipe: LevelRecipe = {
    ...plan.recipe,
    placementStrategy: 'uniform',
    blockingBias: Math.max(1, plan.recipe.blockingBias),
    maxRepairIterations: Math.max(2, plan.recipe.maxRepairIterations),
    directionHeuristics: {
      base: 10,
      blocksExisting: 4,
      createsDependency: 4,
      edgeDistance: 2,
      directionBalance: 6,
      localRepetition: 6,
      towardsInterior: 2,
    },
  };

  let best: { board: BoardData; analysis: PuzzleAnalysis } | undefined;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const rng = new Pcg32(deriveSubSeed(rootSeed, 'generate.fallback', attempt));
    const target = clamp(
      Math.round((plan.activeCells * (300 + attempt * 60)) / 1000),
      1,
      Math.min(plan.activeCells, maxOccupiedCells),
    );
    const candidate = buildCandidate({
      mask: plan.mask,
      recipe: relaxedRecipe,
      rng,
      targetOccupiedCells: target,
    });
    if (candidate.placementCount === 0) continue;
    if (!validateSolution(candidate.board, candidate.constructionSolution).valid) continue;

    const analysis = analyzeBoard(candidate.board);
    if (best === undefined || analysis.occupiedCellCount > best.analysis.occupiedCellCount) {
      best = { board: candidate.board, analysis };
    }
    if (analysis.initialLegalMoves > 0 && analysis.occupiedCellCount >= target) break;
  }
  return best;
}

function generateTutorialLevel(identity: LevelIdentity): GenerationResult {
  const tutorial = getTutorialLevel(identity.levelNumber);
  if (tutorial === undefined) {
    throw new GenerationError(
      'unknown-tutorial-level',
      `No tutorial level ${identity.levelNumber}`,
    );
  }
  const tuning = getTuning(identity.contentVersion);
  const board = tutorialBoard(tutorial);
  const analysis = analyzeBoard(board);
  const rootSeed = deriveRootSeed(identity);
  const band = bandForLevel(identity.levelNumber, tuning);

  const recipe: LevelRecipe = {
    gridWidth: board.width,
    gridHeight: board.height,
    stencilId: 'authored-tutorial',
    stencilCategory: 'tutorial',
    rotation: 0,
    mirrorHorizontal: false,
    mirrorVertical: false,
    targetArrowCount: board.arrows.length,
    targetOccupancyPermille: Math.round(
      (analysis.occupiedCellCount * 1000) / countActiveCells(board),
    ),
    difficultyBand: band,
    archetype: 'broad-branching',
    targetDependencyDepth: analysis.dependencyDepth,
    initialMoveRange: [1, board.arrows.length],
    averageMoveRange: [1, board.arrows.length],
    targetForcedStepRatioPermille: Math.round(analysis.forcedStepRatio * 1000),
    placementStrategy: 'uniform',
    directionHeuristics: {
      base: 1,
      blocksExisting: 0,
      createsDependency: 0,
      edgeDistance: 0,
      directionBalance: 0,
      localRepetition: 0,
      towardsInterior: 0,
    },
    arrowLengthRange: [1, 1],
    blockingBias: 0,
    straightBias: 1,
    turnBias: 1,
    candidateBudget: 0,
    maxPlacementAttempts: 0,
    maxRepairIterations: 0,
    allowFallback: false,
    signature: `tutorial|${identity.levelNumber}`,
  };

  const level = finaliseLevel({
    identity,
    rootSeed,
    recipe,
    board,
    analysis,
    candidateIndex: 0,
    usedFallback: false,
  });

  return {
    level,
    diagnostics: {
      identity,
      rootSeed: toHex64(rootSeed),
      recipe,
      stencilAttempts: [],
      activeCells: countActiveCells(board),
      candidates: [],
      chosenCandidateIndex: 0,
      usedFallback: false,
      holesFilled: 0,
      holeFillingTrials: 0,
    },
  };
}

function finaliseLevel(input: {
  identity: LevelIdentity;
  rootSeed: bigint;
  recipe: LevelRecipe;
  board: BoardData;
  analysis: PuzzleAnalysis;
  candidateIndex: number;
  usedFallback: boolean;
}): GeneratedLevel {
  assertValidBoard(input.board);
  const solution = canonicalSolution(input.board);
  const validation = validateSolution(input.board, solution);
  if (!validation.valid) {
    throw new GenerationError(
      'invalid-canonical-solution',
      `Canonical solution failed validation: ${validation.failureReason ?? 'unknown'}`,
    );
  }
  return {
    identity: input.identity,
    rootSeed: toHex64(input.rootSeed),
    stencilId: input.recipe.stencilId,
    recipe: input.recipe,
    board: input.board,
    canonicalSolution: solution,
    analysis: input.analysis,
    boardHash: computeBoardHash(input.board),
    candidateIndex: input.candidateIndex,
    usedFallback: input.usedFallback,
  };
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}
